import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import FormulaEditor from './FormulaEditor';
import { checkTokens, describeFormula, indexColumns, parseFormula, pickerColumns, serializeFormula } from './formulaModel';
import type { FormulaToken } from './formulaModel';
import { DATA_TYPES, GROUPS, draftOf, emptyDraft, hasErrors, isSystemFormula, sameDraft, validateDraft } from './revenueCalc';
import type { DraftErrors } from './revenueCalc';
import type { RevenueColumn, RevenueDraft } from './revenueApi';

const BORDER = '1px solid #e2e8f0';

const NOTE_SX = { p: 1.5, border: BORDER, borderRadius: '8px', backgroundColor: '#f8fafc' } as const;
const NOTE_TEXT_SX = { color: '#475569', fontSize: '0.84rem' } as const;

interface ColumnFormDialogProps {
  open: boolean;
  /** Редактируемая колонка; null - добавление новой. Форма заполняется при монтировании: на каждое открытие свой key */
  target: RevenueColumn | null;
  columns: RevenueColumn[];
  saving: boolean;
  /** Может менять формулу и блокировку ввода (tables.revenue.formula.edit) */
  canEditFormula: boolean;
  /** Может добавлять колонку в таблицы аптек (tables.revenue.sync.run) */
  canSync: boolean;
  /** Может пересчитывать историю (recalc.preview или recalc.run): для подсказки после смены формулы */
  canRecalc: boolean;
  /** Идет операция над таблицами аптек: сервер сейчас откажет в любом изменении колонок */
  busy: boolean;
  /** Возвращает true, если колонка сохранена и форму можно закрыть */
  onSave: (target: RevenueColumn | null, draft: RevenueDraft) => Promise<boolean>;
  onClose: () => void;
}

const ColumnFormDialog: React.FC<ColumnFormDialogProps> = ({
  open,
  target,
  columns,
  saving,
  canEditFormula,
  canSync,
  canRecalc,
  busy,
  onSave,
  onClose
}) => {
  const isEditing = target !== null;
  const index = useMemo(() => indexColumns(columns), [columns]);
  const sources = useMemo(() => pickerColumns(columns, target ? target.id : null), [columns, target]);

  const [draft, setDraft] = useState<RevenueDraft>(() => (target ? draftOf(target) : emptyDraft()));
  // null - формулу нельзя показать в конструкторе (служебная или с незнакомым содержимым): она остается как есть
  const [tokens, setTokens] = useState<FormulaToken[] | null>(() => (target ? parseFormula(target.formula || '', index) : []));
  // Формула на момент открытия формы: по ней видно, меняли ли ее
  const [baseline] = useState(() => (tokens ? serializeFormula(tokens, index) : ''));
  const [errors, setErrors] = useState<DraftErrors>({});

  // Служебные колонки заполняет система; итоговый остаток на конец дня обязан считаться по формуле
  const systemFilled = !!target && target.isSystemFilled;
  const formulaRequired = !!target && target.isProtected && !target.isSystemFilled;
  const hasFormula = tokens ? tokens.length > 0 : draft.formula.trim() !== '';
  const systemFormula = tokens === null && isSystemFormula(draft.formula);
  const integer = draft.cType.toUpperCase() === 'INT';

  const currentFormula = (): string => {
    if (tokens === null) return draft.formula.trim();
    const text = serializeFormula(tokens, index);
    // Состав формулы не менялся: отправляем прежний текст, чтобы запись формата не выглядела правкой
    return target && text === baseline ? (target.formula || '').trim() : text;
  };
  const formulaChanged = !!target && tokens !== null && serializeFormula(tokens, index) !== baseline;

  const change = (patch: Partial<RevenueDraft>, field?: keyof DraftErrors) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    if (field) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const changeTokens = (next: FormulaToken[]) => {
    setTokens(next);
    // Формулу можно задать только колонке с закрытым вводом: блокируем ввод сами, а не прячем поле
    if (next.length > 0 && draft.InputBlock !== 1) setDraft((prev) => ({ ...prev, InputBlock: 1 }));
    setErrors((prev) => ({ ...prev, formula: undefined }));
  };

  const submit = async () => {
    if (saving || busy) return;
    const formula = currentFormula();
    const clean: RevenueDraft = {
      ...draft,
      cName: draft.cName.trim(),
      displayName: draft.displayName.trim(),
      // Формула есть только у колонки с закрытым вводом
      InputBlock: formula ? 1 : draft.InputBlock,
      formula
    };

    const found = validateDraft(clean, isEditing, columns);
    if (tokens !== null) {
      const problem = checkTokens(tokens).problem;
      if (problem) found.formula = problem;
    }
    if (formulaRequired && !clean.formula) found.formula = 'Итоговый остаток должен считаться по формуле';
    setErrors(found);
    if (hasErrors(found)) return;

    // Ничего не поменялось: сохранять нечего
    if (target && sameDraft(clean, draftOf(target))) {
      onClose();
      return;
    }
    if (await onSave(target, clean)) onClose();
  };

  // Enter в однострочном поле сохраняет форму, как кнопка
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  const setFlag = (key: 'important' | 'statisticsHide' | 'hideInStatTable', on: boolean) => {
    const patch: Partial<RevenueDraft> = {};
    patch[key] = on ? 1 : 0;
    change(patch);
  };

  const flag = (key: 'important' | 'statisticsHide' | 'hideInStatTable', label: string) => (
    <FormControlLabel
      control={<Checkbox size="small" checked={draft[key] === 1} onChange={(event) => setFlag(key, event.target.checked)} />}
      label={label}
    />
  );

  const renderFormula = () => {
    if (tokens === null) {
      return (
        <Box sx={NOTE_SX}>
          <Typography sx={NOTE_TEXT_SX}>
            {systemFormula
              ? 'Формулу задает система, менять ее нельзя.'
              : 'Эту формулу нельзя показать в конструкторе: в ней есть незнакомые элементы.'}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.82rem', mt: 0.5 }}>
            Значение: <code>{draft.formula}</code>
          </Typography>
          {!systemFormula && canEditFormula && (
            <Button
              size="small"
              onClick={() => setTokens([])}
              sx={{ mt: 1, px: 0, textTransform: 'none', fontWeight: 600, color: '#1d4ed8' }}
            >
              Заменить формулой из конструктора
            </Button>
          )}
        </Box>
      );
    }
    if (!canEditFormula) {
      return (
        <Box sx={NOTE_SX}>
          <Typography sx={NOTE_TEXT_SX}>
            {tokens.length > 0
              ? describeFormula(tokens, index)
              : draft.InputBlock === 1
                ? 'Формулы нет: колонку никто не заполняет'
                : 'Формулы нет: значение вводят в отчете вручную'}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.5 }}>
            Менять формулы могут только сотрудники с правом на изменение формул.
          </Typography>
        </Box>
      );
    }
    return (
      <FormulaEditor
        tokens={tokens}
        onChange={changeTokens}
        sources={sources}
        index={index}
        integer={integer}
        error={errors.formula}
      />
    );
  };

  return (
    <ConfirmDialog
      open={open}
      title={isEditing ? 'Изменить колонку' : 'Добавить колонку'}
      confirmText={isEditing ? 'Сохранить' : 'Добавить'}
      loading={saving}
      confirmDisabled={busy}
      maxWidth="md"
      onConfirm={submit}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        {busy && (
          <Box sx={{ ...NOTE_SX, backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
            <Typography sx={{ color: '#92400e', fontSize: '0.84rem' }}>
              Идет обслуживание таблиц выручки. Сохранить изменения можно после завершения операции.
            </Typography>
          </Box>
        )}

        <TextField
          size="small"
          fullWidth
          required
          autoFocus={!isEditing}
          label="Имя в БД"
          value={draft.cName}
          disabled={isEditing}
          onChange={(event) => change({ cName: event.target.value }, 'cName')}
          onKeyDown={handleKeyDown}
          error={!!errors.cName}
          helperText={
            errors.cName ||
            (isEditing
              ? 'Имя и тип нельзя менять: колонка уже есть в таблицах аптек'
              : 'Латинские буквы, цифры и «_». Если колонки еще нет в таблицах аптек, портал добавит ее сам')
          }
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="Тип данных"
            value={draft.cType}
            disabled={isEditing}
            onChange={(event) => change({ cType: event.target.value })}
          >
            {DATA_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            fullWidth
            label="Группа"
            value={draft.sortByGroup}
            onChange={(event) => change({ sortByGroup: event.target.value })}
          >
            {GROUPS.map((group) => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          size="small"
          fullWidth
          required
          autoFocus={isEditing}
          label="Название в отчетах"
          value={draft.displayName}
          onChange={(event) => change({ displayName: event.target.value }, 'displayName')}
          onKeyDown={handleKeyDown}
          error={!!errors.displayName}
          helperText={errors.displayName || 'Так колонка называется в отчетах аптек'}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 }}>
          {flag('important', 'Обязательное поле')}
          {flag('statisticsHide', 'Скрыть в итогах')}
          {flag('hideInStatTable', 'Не показывать в статистике')}
        </Box>

        {systemFilled ? (
          <Box sx={NOTE_SX}>
            <Typography sx={NOTE_TEXT_SX}>Колонку заполняет система: блокировку ввода и формулу менять нельзя.</Typography>
            {hasFormula && (
              <Typography sx={{ color: '#64748b', fontSize: '0.82rem', mt: 0.5 }}>
                Значение: <code>{draft.formula}</code>
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={draft.InputBlock === 1 || hasFormula}
                    disabled={!canEditFormula || formulaRequired || hasFormula}
                    onChange={(event) => change({ InputBlock: event.target.checked ? 1 : 0 })}
                  />
                }
                label="Заблокировать ввод"
              />
              {canEditFormula && hasFormula && !formulaRequired && (
                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', ml: 4, mt: -0.5 }}>
                  Чтобы разблокировать ввод, очистите формулу
                </Typography>
              )}
              {draft.InputBlock === 1 && !hasFormula && (
                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', ml: 4, mt: -0.5 }}>
                  Пока формулы нет, колонку никто не заполняет
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.88rem', mb: 0.5 }}>Формула</Typography>
              {canEditFormula && tokens !== null && (
                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mb: 1 }}>
                  Значение колонки считается автоматически, поэтому с формулой ввод в отчете закрывается сам
                </Typography>
              )}
              {renderFormula()}
            </Box>
          </>
        )}

        {target && target.usedIn.length > 0 && (
          <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
            Колонка входит в формулы: {target.usedIn.join(', ')}
          </Typography>
        )}

        {formulaChanged && (
          <Box sx={NOTE_SX}>
            <Typography sx={NOTE_TEXT_SX}>
              Прежние значения в таблицах аптек автоматически не изменятся.
              {canRecalc ? ' Пересчитать историю можно кнопкой «Пересчитать историю» на странице таблицы.' : ''}
            </Typography>
          </Box>
        )}

        {!isEditing && (
          <Box sx={NOTE_SX}>
            <Typography sx={NOTE_TEXT_SX}>
              {canSync
                ? 'Колонка, которой нет в таблицах аптек, добавляется в них в фоне. Пока это идет, работа с данными аптек временно приостанавливается.'
                : 'У вас нет права добавлять колонки в таблицы аптек: колонка создастся, только если она уже есть во всех таблицах.'}
            </Typography>
          </Box>
        )}
      </Box>
    </ConfirmDialog>
  );
};

export default ColumnFormDialog;
